import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { KpiObjectType } from '@/api/entities/kpi';
import { resolveLanguageCode } from '@/utils/language.utils';
import { TKpiDistributionSchema } from '@/schemas/kpi-distribution/kpi-distribution.schema';
import { TKpiEvaluationSchema } from '@/schemas/kpi-evaluation/kpi-evaluation.schema';
import { KpiModel, KpiModelSummary } from '@/type/kpi-dashboard.types';
import { getKpiModelSummary } from '@/utils/kpi-dashboard.utils';
import { useGetAllKpiDistributionsByType } from './kpi-distribution.hooks';
import { useGetKpiEvaluationByType } from './kpi-evaluation.hooks';

/**
 * Loads the two KPI collections for one model type and assembles what the
 * dashboard renders.
 *
 * The per-class rows arrive already sorted by ascending IoU from the API, so
 * the histogram and the top/flop rankings are derived from them here. The
 * headline aggregates, however, are taken from the stored KpiEvaluation rather
 * than recomputed, so the page always shows exactly what the evaluation
 * pipeline published.
 */
export const useGetKpiModel = (type: KpiObjectType) => {
  const { i18n } = useTranslation();

  // Keyed into the distribution query, so switching language refetches the
  // names instead of serving the previous language from cache.
  const languageCode = resolveLanguageCode(i18n?.language);

  const {
    data: evaluationResponse,
    isLoading: isLoadingEvaluation,
    isError: isErrorEvaluation,
  } = useGetKpiEvaluationByType(type);

  const {
    data: distributionResponse,
    isLoading: isLoadingDistributions,
    isError: isErrorDistributions,
  } = useGetAllKpiDistributionsByType(type, languageCode);

  const evaluation = evaluationResponse?.data as
    TKpiEvaluationSchema | undefined;
  const distributions = distributionResponse?.data as
    TKpiDistributionSchema[] | undefined;

  const model = useMemo<KpiModel | undefined>(() => {
    if (!evaluation || !distributions) return undefined;

    return {
      evaluatedImages: evaluation?.evaluatedImages ?? 0,
      updatedAt: evaluation?.updatedAt ?? undefined,
      classes: (distributions ?? []).map((distribution) => ({
        id: distribution?.id ?? '',
        // TranslatedEquipment / TranslatedDamage is the source of truth for
        // the label; the denormalised name only covers untranslated objects.
        name: distribution?.translatedName ?? distribution?.name ?? '',
        iou: distribution?.iouScore ?? 0,
        annotations: distribution?.annotationCount ?? 0,
      })),
    };
  }, [evaluation, distributions]);

  const summary = useMemo<KpiModelSummary | undefined>(() => {
    if (!model || !evaluation) return undefined;

    return {
      ...getKpiModelSummary(model),
      classCount: evaluation?.numberOfClasses ?? 0,
      mIou: evaluation?.globalIoUScore ?? 0,
      tierCounts: {
        strong: evaluation?.strongCount ?? 0,
        moderate: evaluation?.moderateCount ?? 0,
        weak: evaluation?.weakCount ?? 0,
      },
    };
  }, [model, evaluation]);

  return {
    model,
    summary,
    isLoading: isLoadingEvaluation || isLoadingDistributions,
    isError: isErrorEvaluation || isErrorDistributions,
  };
};
